import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { useSelector } from 'react-redux'
import { ApplicationState } from '@/state/root.models'

import {
  BonusTriggerType,
  BonusType,
  ManualWalletAssign,
  RolloverTypeEnum,
} from '@/enums'
import {
  REGEX_INPUT,
  REGEX_NUMBERS,
  REGEX_NUMBER_FLOAT,
  REGEX_UUID,
} from '@/constants/regex'

export type BonusCodeValue = {
  name: string
  code: string
}

export type ProviderValue = {
  value: number
  label: string
}

export type FormValuesManualWallet = {
  bonusType: BonusType
  bonusTrigger: BonusTriggerType
  byPlayer: ManualWalletAssign
  playerId: string
  username: string
  depositId: string
  depositAmount: string
  bonusCode: BonusCodeValue
  comment: string
  bonusAmount: string
  rollover: string
  rolloverType: string
  maxWin: string
  providers: ProviderValue[]
}

export const useWalletsSchema = () => {
  const { t } = useTranslation()

  const location = useLocation()

  const { playerCard } = useSelector(
    ({ playerCard }: ApplicationState) => playerCard,
  )

  const isPlayerCard = location.pathname.includes('/player-card')

  const urlParams = new URLSearchParams(window.location.search)
  const initialValuesManualWallet: FormValuesManualWallet = {
    bonusType: BonusType.CASINO_STANDARD,
    bonusTrigger: undefined,
    byPlayer: isPlayerCard
      ? urlParams.get('playerId')
        ? ManualWalletAssign.PLAYER
        : ManualWalletAssign.USERNAME
      : ManualWalletAssign.USERNAME,
    playerId: isPlayerCard ? playerCard?.id || '' : '',
    username: isPlayerCard ? playerCard?.username || '' : '',
    // playerId: isPlayerCard ? urlParams.get('playerId') || undefined : '',
    // username: isPlayerCard ? urlParams.get('username') || undefined : '',
    depositId: '',
    depositAmount: '',
    bonusCode: { code: '', name: '' },
    comment: '',
    bonusAmount: '',
    rollover: '',
    rolloverType: RolloverTypeEnum.COEFFICIENT,
    maxWin: '',
    providers: [],
  }

  const requiredString = Yup.string().required(t('error.requiredField'))

  const validationSchemaManualWallet = Yup.object().shape({
    byPlayer: requiredString,
    playerId: Yup.string().when('byPlayer', {
      is: (value: string) => value && value === ManualWalletAssign.PLAYER,
      then: Yup.string()
        .required(t('error.requiredField'))
        .test(
          'is-valid-player-id',
          t('messages.formatError', {
            field: t('labels.playerId'),
          }),
          (value) => {
            if (!value) return false
            return REGEX_NUMBERS.test(value) || REGEX_UUID.test(value)
          },
        )
        .test(
          'is-player-found',
          t('error.playerNotFound', 'Player not found'),
          function () {
            return !!this.parent.username
          },
        ),
    }),
    username: Yup.string().when('byPlayer', {
      is: (value: string) => value && value === ManualWalletAssign.USERNAME,
      then: Yup.string()
        .required(t('error.requiredField'))
        .matches(REGEX_INPUT, t('error.regexInput'))
        .test(
          'is-player-found',
          t('error.playerNotFound', 'Player not found'),
          function () {
            return !!this.parent.playerId
          },
        ),
    }),
    depositId: Yup.string().when('bonusTrigger', {
      is: (value: number) =>
        value &&
        (value === BonusTriggerType.DEPOSIT_BY_CLICK ||
          value === BonusTriggerType.DEPOSIT_BY_CODE),
      then: Yup.string()
        .required(t('error.requiredField'))
        .matches(
          REGEX_NUMBERS,
          t('messages.formatError', {
            field: t('wallet.depositId'),
          }),
        ),
    }),
    depositAmount: Yup.string().when('bonusTrigger', {
      is: (value: number) =>
        value &&
        (value === BonusTriggerType.DEPOSIT_BY_CLICK ||
          value === BonusTriggerType.DEPOSIT_BY_CODE),
      then: Yup.string()
        .required(t('error.requiredField'))
        .matches(
          REGEX_NUMBER_FLOAT,
          t('messages.formatError', {
            field: t('wallet.depositAmount'),
          }),
        ),
    }),
    bonusCode: Yup.object().when('bonusTrigger', {
      is: (value: number) => value && value !== BonusTriggerType.MANUAL,
      then: Yup.object().shape({
        code: Yup.string().required(t('error.requiredField')),
      }),
    }),
    bonusAmount: Yup.string().when('bonusTrigger', {
      is: (value: number) => value && value === BonusTriggerType.MANUAL,
      then: Yup.string()
        .required(t('error.requiredField'))
        .matches(
          REGEX_NUMBER_FLOAT,
          t('messages.formatError', {
            field: t('labels.bonusAmount'),
          }),
        ),
    }),
    rollover: Yup.string().when('bonusTrigger', {
      is: (value: number) => value && value === BonusTriggerType.MANUAL,
      then: Yup.string()
        .required(t('error.requiredField'))
        .matches(
          REGEX_NUMBER_FLOAT,
          t('messages.formatError', {
            field: t('labels.rolloverAmount'),
          }),
        ),
    }),
    maxWin: Yup.string().when('bonusTrigger', {
      is: (value: number) => value && value === BonusTriggerType.MANUAL,
      then: Yup.string()
        .required(t('error.requiredField'))
        .matches(
          REGEX_NUMBER_FLOAT,
          t('messages.formatError', {
            field: t('wallet.manualMaxWin'),
          }),
        ),
    }),
    comment: Yup.string().when('bonusTrigger', {
      is: (value: number) => value && value === BonusTriggerType.MANUAL,
      then: requiredString.max(90, ({ max }) =>
        t('messages.inputMaxLength', {
          field: t('labels.comment'),
          max,
        }),
      ),
    }),
    providers: Yup.array().when('bonusTrigger', {
      is: (value: number) => value && value === BonusTriggerType.MANUAL,
      then: Yup.array().min(1, t('error.requiredField')),
    }),
  })

  return {
    initialValuesManualWallet,
    validationSchemaManualWallet,
  }
}
